/**
 * TopNav organization selector — the group-admin companion to the
 * ClientSelector (agent picker). Lives in the breadcrumb between the
 * group crumb and the agent/section crumb:
 *
 *     [Group] › [Org ▾] › [Agent ▾] › [Section]
 *
 * Only group admins see this. Org admins already have a fixed org on
 * their JWT and have no choice to make here.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth }                              from "@features/auth/AuthContext";
import { usePickerOrgs, useSelectedOrg }        from "@features/endpoint-inventory/hooks/useOrgPicker";
import type { PickerOrg }                       from "@features/endpoint-inventory/api/orgs.schemas";
import styles                                   from "./OrgSelector.module.scss";

/** Controlled-mode props, same contract as ClientSelector. When supplied,
 *  the parent (TopNav) coordinates open-state across all its menus so
 *  opening one closes the others. Omitted → fall back to internal state. */
interface OrgSelectorProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const OrgSelector: React.FC<OrgSelectorProps> = ({ isOpen, onOpenChange }) => {
  const { user } = useAuth();
  // group_id not yet on the formal User type — cast locally (same workaround
  // TopNav uses for group_name). Fall back to the sidecar localStorage key.
  const groupId =
    (user as { group_id?: string } | null | undefined)?.group_id ??
    (() => { try { return localStorage.getItem("specta.group_id") ?? ""; } catch { return ""; } })();

  const { selected, select } = useSelectedOrg();

  // Controlled when parent passes isOpen+onOpenChange, uncontrolled otherwise.
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;
  const setOpen = (next: boolean | ((o: boolean) => boolean)): void => {
    const value = typeof next === "function" ? next(open) : next;
    if (isOpen !== undefined) onOpenChange?.(value);
    else setInternalOpen(value);
  };

  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const list = usePickerOrgs(groupId);

  // Click-outside + Escape to close — identical pattern to ClientSelector.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
    else setSearch("");
  }, [open]);

  const orgs = useMemo<PickerOrg[]>(() => {
    const rows = list.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q === ""
      ? rows
      : rows.filter((o) => o.name.toLowerCase().includes(q));

    // Float the currently-picked org to the top of what's left.
    if (!selected?.org_id) return filtered;
    const cur = filtered.find((o) => o.org_id === selected.org_id);
    if (!cur) return filtered;
    return [cur, ...filtered.filter((o) => o.org_id !== selected.org_id)];
  }, [list.data, selected, search]);

  const pick = (o: PickerOrg) => {
    select({ org_id: o.org_id, name: o.name });
    setOpen(false);
  };

  if (!groupId) return null;

  return (
    <div className={styles.wrap} ref={rootRef}>
      <button
        className={`${styles.crumb} ${open ? styles.open : ""} ${selected ? styles.picked : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected ? selected.name : "Pick an organization"}
      >
        <i className={`fas fa-building ${styles.icon}`} />
        {selected ? (
          <span className={styles.name}>{selected.name}</span>
        ) : (
          <span className={styles.pickText}>Pick an organization</span>
        )}
        <i className={`fas fa-chevron-down ${styles.caret}`} />
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox">
          <div className={styles.search}>
            <i className="fas fa-magnifying-glass" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search organization…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.list}>
            {list.isFetching && !list.data && (
              <div className={styles.state}>Loading…</div>
            )}
            {list.error && !list.data && (
              <div className={styles.state}>Couldn&apos;t load organizations.</div>
            )}
            {list.data && orgs.length === 0 && (
              <div className={styles.state}>
                {search ? `No organizations match "${search}"` : "No organizations in this group yet."}
              </div>
            )}
            {orgs.map((o) => {
              const isSelected = selected?.org_id === o.org_id;
              return (
                <button
                  key={o.org_id}
                  className={`${styles.row} ${isSelected ? styles.rowSelected : ""}`}
                  onClick={() => pick(o)}
                >
                  {/* <i className={`fas fa-building ${styles.rowIcon}`} /> */}
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{o.name}</span>
                    {/* <span className={styles.rowMeta}>{o.org_id}</span> */}
                  </div>
                  {isSelected && <i className={`fas fa-check ${styles.check}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
